import React from 'react';
import {
  Gavel as BidIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  SwapHoriz as CounterIcon,
  Done as CompletedIcon,
  Block as CancelledIcon,
  PersonAdd as TeamIcon,
  Verified as VerifiedIcon,
  Campaign as SystemIcon,
  RateReview as ReviewIcon,
} from '../ui/icons';
import { useTranslation } from 'react-i18next';
import { AppNotification, NotificationType } from '../../types/notification';

interface NotificationItemProps {
  notification: AppNotification;
  onClick: (notification: AppNotification) => void;
}

const iconByType: Record<NotificationType, React.ReactElement> = {
  new_bid: <BidIcon size={18} />,
  bid_accepted: <AcceptIcon size={18} />,
  bid_rejected: <RejectIcon size={18} />,
  bid_counter_offer: <CounterIcon size={18} />,
  offer_completed: <CompletedIcon size={18} />,
  offer_cancelled: <CancelledIcon size={18} />,
  team_member_joined: <TeamIcon size={18} />,
  company_verified: <VerifiedIcon size={18} />,
  system_announcement: <SystemIcon size={18} />,
  company_review_request: <ReviewIcon size={18} />,
};

const getRelativeTime = (
  dateString: string,
  t: (key: string, opts?: Record<string, string | number>) => string,
): string => {
  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return t('notifications.time.just_now');
  if (diffMin < 60) return t('notifications.time.minutes_ago', { count: diffMin });
  if (diffHr < 24) return t('notifications.time.hours_ago', { count: diffHr });
  return t('notifications.time.days_ago', { count: diffDay });
};

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClick }) => {
  const { t } = useTranslation();
  const icon = iconByType[notification.type] ?? <SystemIcon size={18} />;

  return (
    <button
      onClick={() => onClick(notification)}
      className={[
        'w-full text-left flex items-center gap-3 px-4 py-3 border-l-[3px] transition-colors duration-150',
        notification.read
          ? 'border-transparent hover:bg-primary/10'
          : 'border-primary bg-primary/[0.04] hover:bg-primary/10',
      ].join(' ')}
    >
      {/* Icon */}
      <div className="p-1.5 rounded-xl flex-shrink-0 flex items-center justify-center bg-primary/10 text-primary">
        {icon}
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm truncate text-content ${notification.read ? 'font-medium' : 'font-bold'}`}>
          {t(notification.title, notification.titleParams || {})}
        </p>
        <p className="text-xs text-muted truncate mt-0.5">
          {t(notification.body, notification.bodyParams || {})}
        </p>
        <p className="text-[0.65rem] font-semibold mt-0.5 text-primary/85">
          {getRelativeTime(notification.createdAt, t)}
        </p>
      </div>

      {/* Unread dot */}
      {!notification.read && (
        <div className="w-2 h-2 rounded-full flex-shrink-0 bg-primary ring-4 ring-primary/30" />
      )}
    </button>
  );
};

export default NotificationItem;
